// Angular imports
import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild
} from '@angular/core';

// 3rd Party imports
import { DxTreeViewComponent } from 'devextreme-angular';

// CC-Common imports
import { RxjsUtils } from '@therealmanage/ciranet-ui-common';

// Package.json import
import packageInfo from '../../../../package.json';

// Service imports
import { ScreenService } from '../../_services/shared-services/screen.service';

// Model imports
import { SideNavMenuInfo } from '../../_models/side-nav-menu-info';

// Constant imports
import { ImageConstants } from '../../_constants/image-constants';

@Component({
  selector: 'app-side-navigation-menu',
  templateUrl: './side-navigation-menu.component.html',
  styleUrls: ['./side-navigation-menu.component.scss'],
  standalone: false
})
export class SideNavigationMenuComponent {
  @Input()
  set topItems(value: SideNavMenuInfo[]) {
    this._topMenus = value;
    if (value.length > 0) {
      this.homeMenu = value[0];
    }

    if (value.length > 1) {
      this.paymentMenu = value[1];
      this.paymentSubmenu = this.paymentMenu.items;
    }

    if (value.length > 2) {
      this.propertyMenu = value[2];
      this.propertySubmenu = this.propertyMenu.items;
    }

    if (value.length > 3) {
      this.communityMenu = value[3];
      this.communitySubmenu = this.communityMenu.items;
    }

    this._programmaticSelection = true;
    this.selectMenuItem();
    this._programmaticSelection = false;
  }

  get topItems(): SideNavMenuInfo[] {
    return this._topMenus;
  }

  @Input()
  bottomItems: SideNavMenuInfo[];

  @Input()
  set selectedItem(value: string) {
    if (!!value && value.length > 0) {
      this._selectedItem = value;
      if (!!this.homeMenu) {
        this.clearMenuSelection();
        this.selectMenuItem(value);
        if (this.bottomMenuItems && this.bottomMenuItems.instance) {
          this.bottomMenuItems.instance.unselectAll();
          this.bottomMenuItems.instance.selectItem(value);
        }
      }
    }
  }

  get selectedItem(): string {
    return this._selectedItem;
  }

  @Input()
  set compactMode(val) {
    this._compactMode = val;
  }

  get compactMode() {
    return this._compactMode;
  }

  @Output() selectedItemChanged = new EventEmitter<object>();
  @Output() openMenu = new EventEmitter<any>();

  @ViewChild('topMenuItems', { static: false })
  topMenuItems: DxTreeViewComponent;

  @ViewChild('bottomMenuItems', { static: false })
  bottomMenuItems: DxTreeViewComponent;

  appVersion = packageInfo.version;
  selectedChildNode: string;
  ccLogo: string;
  isXSmallScreen: boolean = false;
  isSmallScreen: boolean = false;
  poweredByCCLogo: string;
  homeMenu: SideNavMenuInfo;
  paymentMenu: SideNavMenuInfo;
  propertyMenu: SideNavMenuInfo;
  communityMenu: SideNavMenuInfo;
  paymentSubmenu: SideNavMenuInfo[];
  propertySubmenu: SideNavMenuInfo[];
  communitySubmenu: SideNavMenuInfo[];

  private _compactMode: boolean = false;
  private _topMenus: SideNavMenuInfo[];
  private _selectedItem: string;
  private _programmaticSelection = false;

  constructor(private screenService: ScreenService) {
    this.ccLogo = ImageConstants.ccLogo;
    this.poweredByCCLogo = ImageConstants.poweredByCCLogo;
    this.isXSmallScreen = this.screenService.isXSmallScreen();
    this.isSmallScreen = this.screenService.isSmallScreen();
    RxjsUtils.safeSubscribe(this.screenService.changed, () => {
      this.isSmallScreen = this.screenService.isSmallScreen();
      this.isXSmallScreen = this.screenService.isXSmallScreen();
    });
  }

  onParentMenuItemClick(item: SideNavMenuInfo): void {
    if (!item.selected) {
      this.clearMenuSelection(!!item.items);
      item.selected = true;
      if (!item.items) {
        this.selectedItemChanged.emit({ itemData: item });
      }
    }
    if (!!item.items && !this._programmaticSelection) {
      this.openMenu.next(item);
    }
  }

  onChildMenuItemClick(item: SideNavMenuInfo, subitem: SideNavMenuInfo) {
    if (!subitem.selected) {
      this.clearMenuSelection();
      item.selected = true;
      subitem.selected = true;
      this.selectedChildNode = subitem.text;
      this.selectedItemChanged.emit({ itemData: subitem });
    }
  }

  onItemClick(event) {
    // disabling all top menu items when bottom item is selected.
    this.clearMenuSelection();
    if (event.node.key === '/contact-us') {
      this.bottomMenuItems.instance.selectItem(event.node.key);
    }
    this.selectedItemChanged.emit(event);
  }

  onMenuInitialized(event) {
    event.component.option('deferRendering', false);
  }

  updateSelection(event) {
    const nodeClass = 'dx-treeview-node';
    const selectedClass = 'dx-state-selected';
    const element: HTMLElement = event.element;

    let selectedNode = element.querySelector(`.${nodeClass}.${selectedClass}`);
    while (!!selectedNode && !!selectedNode.parentElement) {
      if (selectedNode.classList.contains(nodeClass)) {
        selectedNode.classList.add(selectedClass);
      }
      selectedNode = selectedNode.parentElement;
    }
  }

  getCiraConnectPowerByStyle() {
    if (this.screenService.isXSmallScreen()) {
      return { width: '145px' };
    }
    return {};
  }

  private clearMenuSelection(skipItems: boolean = false): void {
    this.homeMenu.selected = false;
    this.paymentMenu.selected = false;
    this.propertyMenu.selected = false;
    this.communityMenu.selected = false;

    if (skipItems === false) {
      this.selectedChildNode = null;
      this.paymentMenu.items.forEach((node) => (node.selected = false));
      this.propertyMenu.items.forEach((node) => (node.selected = false));
      this.communityMenu.items.forEach((node) => (node.selected = false));
    }

    if (this.bottomMenuItems && this.bottomMenuItems.instance) {
      this.bottomMenuItems.instance.unselectAll();
    }
  }

  trackByPath(_: number, item: SideNavMenuInfo): any {
    return item.path;
  }

  private selectMenuItem(key?: string): void {
    let menuFound = false;
    for (const item of this.topItems) {
      if (
        item.selected ||
        (!!item.path && (item.path === key || item.path === this.selectedItem))
      ) {
        this.onParentMenuItemClick(item);
        menuFound = true;
      }
      if (item.items) {
        for (const subitem of item.items) {
          if (
            subitem.selected ||
            (!!subitem.path &&
              (subitem.path === key || subitem.path === this.selectedItem))
          ) {
            this.onChildMenuItemClick(item, subitem);
            menuFound = menuFound && true;
            break;
          }
        }
      }
      if (menuFound) {
        break;
      }
    }
  }
}
